import { strToU8, zipSync } from "fflate";
import type { PreflightReportAsset, PreflightReportDetail } from "../types/preflight.types";
import {
  assetExportFilename,
  buildReportJson,
  buildReportText,
  exportFilename,
  singleAssetReport,
} from "./export-report";
import { buildReportWorkbookArray } from "./report-workbook";
import { downloadBytes } from "./download";

/** Client-side export formats that can be bundled one-file-per-asset into a zip. */
export type AssetBundleFormat = "xlsx" | "json" | "txt";

const ZIP_MIME = "application/zip";

/** Build a single asset's report file (scoped + signed-URL-free) in the chosen format. */
export function buildAssetFile(
  report: PreflightReportDetail,
  asset: PreflightReportAsset,
  format: AssetBundleFormat,
): Uint8Array {
  const scoped = singleAssetReport(report, asset);
  switch (format) {
    case "json":
      return strToU8(buildReportJson(scoped));
    case "txt":
      return strToU8(buildReportText(scoped));
    case "xlsx":
      return new Uint8Array(buildReportWorkbookArray(scoped));
  }
}

/** Disambiguate a filename against names already used in the archive ("name.csv" → "name-2.csv"). */
function uniqueName(name: string, used: Set<string>): string {
  if (!used.has(name)) {
    used.add(name);
    return name;
  }
  const dot = name.lastIndexOf(".");
  const base = dot === -1 ? name : name.slice(0, dot);
  const ext = dot === -1 ? "" : name.slice(dot);
  let index = 2;
  let candidate = `${base}-${index}${ext}`;
  while (used.has(candidate)) {
    index += 1;
    candidate = `${base}-${index}${ext}`;
  }
  used.add(candidate);
  return candidate;
}

/** Zip one per-asset report file (in `format`) for every asset in the report. */
export function buildReportAssetsZip(
  report: PreflightReportDetail,
  format: AssetBundleFormat,
): Uint8Array {
  const files: Record<string, Uint8Array> = {};
  const used = new Set<string>();
  for (const asset of report.assets) {
    const name = uniqueName(assetExportFilename(report, asset, format), used);
    files[name] = buildAssetFile(report, asset, format);
  }
  return zipSync(files);
}

/** Archive filename, e.g. "acme-co-r1-2026-06-14-csv-per-asset.zip". */
function bundleFilename(
  report: PreflightReportDetail,
  format: AssetBundleFormat,
): string {
  return exportFilename(report, "zip").replace(/\.zip$/, `-${format}-per-asset.zip`);
}

/** Download every asset's report as separate files bundled into a single zip. */
export function exportReportAssetsAsZip(
  report: PreflightReportDetail,
  format: AssetBundleFormat,
): void {
  downloadBytes(
    buildReportAssetsZip(report, format),
    bundleFilename(report, format),
    ZIP_MIME,
  );
}
